import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogTitle, LinearProgress } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import EditDialogStyle from "styles/application/EditDialogStyle";
import { getLocalStorageData } from "helpers/utils";
import { showError } from "../../../../../redux/common/actions";
import { createSiteAssetRegister, patchSiteAssetRegister } from "services/clients/sites/siteAssetRegisters";

const ET = EditDialogStyle();

const useStyles = makeStyles()(() => ({
	paper: { minWidth: "90%" },
}));

const initialState = {
	id: null,
	assetNumber: "",
	reference: "",
	description: "",
	plannerGroup: "",
	workCenter: "",
};

const AssetDialog = ({
	open,
	closeHandler,
	onSuccess,
	getError,
	isEdit = false,
	showSAPColumns = false,
	siteId,
	data = {},
	testIdPrefix,
}) => {
	const { classes } = useStyles();
	const dispatch = useDispatch();
	const { customCaptions } = getLocalStorageData("me");
	const [loading, setLoading] = useState(false);
	const [input, setInput] = useState(initialState);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (open) {
			setInput({ ...initialState, ...data });
			setErrors({});
		}
	}, [open, data]);

	const closeOverride = () => {
		closeHandler();
		setInput(initialState);
		setErrors({});
	};

	const validate = () => {
		const nextErrors = {};
		const assetLabel = customCaptions?.asset || "Asset";
		const referenceLabel = customCaptions?.assetReference || "Reference";
		const hasAssetNumber = !!input.assetNumber?.trim();
		const hasReference = !!input.reference?.trim();

		if (showSAPColumns) {
			if (!isEdit && !hasReference && !hasAssetNumber) {
				nextErrors.assetNumber = `${assetLabel} Number is required when ${referenceLabel} is blank`;
			}
		} else if (!hasAssetNumber) {
			nextErrors.assetNumber = `${assetLabel} Number is required`;
		}

		if (!input.description?.trim()) {
			nextErrors.description = "Description is required";
		}

		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const handleEnterPress = (e) => {
		if (e.keyCode === 13) {
			handleSave();
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setInput((prev) => ({ ...prev, [name]: value }));
	};

	const handleSave = async () => {
		setLoading(true);
		setErrors({});
		try {
			if (!validate()) {
				setLoading(false);
				return;
			}

			let result;

			if (isEdit) {
				const ops = [
					{ op: "replace", path: "assetNumber", value: input.assetNumber },
					{ op: "replace", path: "description", value: input.description },
					{ op: "replace", path: "plannerGroup", value: input.plannerGroup || null },
					{ op: "replace", path: "workCenter", value: input.workCenter || null },
				];
				const resp = await patchSiteAssetRegister(input.id, ops);
				result = { success: resp.status, data: resp.data };
			} else {
				const payload = {
					siteId: +siteId,
					assetNumber: input.assetNumber,
					reference: input.reference,
					description: input.description,
					plannerGroup: input.plannerGroup || null,
					workCenter: input.workCenter || null,
				};
				const resp = await createSiteAssetRegister(payload);
				result = { success: resp.status, data: resp.data };
			}

			if (result.success) {
				await onSuccess();
				closeOverride();
			} else {
				if (result.data?.detail) {
					getError(result.data.detail);
				} else if (result.data?.errors) {
					setErrors(result.data.errors);
				} else {
					getError("Failed to save");
				}
			}
		} catch (err) {
			dispatch(showError("Failed to save"));
		} finally {
			setLoading(false);
		}
	};

	const dialogTitle = useMemo(() => {
		const assetLabel = customCaptions?.asset || "Asset";
		if (isEdit) {
			return `Edit ${assetLabel}`;
		}
		return `Add New ${assetLabel}`;
	}, [isEdit, customCaptions]);

	const fields = useMemo(() => {
		const hasAssetNumber = !!input.assetNumber?.trim();
		const hasReference = !!input.reference?.trim();
		// In SAP-add mode either identifier satisfies the backend; mark both as
		// required until the user supplies one, then clear the star on the other.
		const sapAddNeedsIdentifier = showSAPColumns && !isEdit && !hasAssetNumber && !hasReference;

		const base = [
			{
				name: "assetNumber",
				label: customCaptions?.asset || "Asset Number",
				required: !showSAPColumns || sapAddNeedsIdentifier,
			},
			{ name: "description", label: "Description", required: true },
		];

		if (showSAPColumns) {
			base.splice(1, 0, {
				name: "reference",
				label: customCaptions?.assetReference || "Reference",
				required: sapAddNeedsIdentifier,
				readOnly: isEdit,
			});
			base.push(
				{ name: "plannerGroup", label: customCaptions?.assetPlannerGroup || "Planner Group", required: false },
				{ name: "workCenter", label: customCaptions?.assetMainWorkCenter || "Work Center", required: false },
			);
		}

		return base;
	}, [showSAPColumns, customCaptions, isEdit, input.assetNumber, input.reference]);

	const leftFields = fields.slice(0, Math.ceil(fields.length / 2));
	const rightFields = fields.slice(Math.ceil(fields.length / 2));

	const renderField = (field) => (
		<div
			key={field.name}
			data-testid={testIdPrefix ? `${testIdPrefix}-field-${field.name}` : undefined}
			style={{ display: "flex", flexDirection: "column", marginBottom: 20 }}
		>
			<ET.NameLabel>
				{field.label}
				{field.required && <ET.RequiredStar>*</ET.RequiredStar>}
			</ET.NameLabel>
			<ET.NameInput
				data-testid={testIdPrefix ? `${testIdPrefix}-input-${field.name}` : undefined}
				name={field.name}
				error={Boolean(errors?.[field.name])}
				helperText={errors?.[field.name] || null}
				variant="outlined"
				value={input?.[field.name] ?? ""}
				disabled={field.readOnly}
				onChange={handleChange}
				onKeyDown={handleEnterPress}
				fullWidth
				sx={{
					"& .MuiInputBase-input.Mui-disabled": {
						WebkitTextFillColor: "#000000",
					},
				}}
			/>
		</div>
	);

	return (
		<Dialog
			classes={{ paper: classes.paper }}
			open={open}
			onClose={closeOverride}
			aria-labelledby="asset-dialog-title"
			data-testid={testIdPrefix ? `${testIdPrefix}-dialog` : undefined}
		>
			{loading && <LinearProgress />}
			<ET.ActionContainer>
				<DialogTitle id="asset-dialog-title">
					<ET.HeaderText data-testid={testIdPrefix ? `${testIdPrefix}-title` : undefined}>{dialogTitle}</ET.HeaderText>
				</DialogTitle>
				<ET.ButtonContainer>
					<ET.CancelButton data-testid={testIdPrefix ? `${testIdPrefix}-cancel` : undefined} onClick={closeOverride} variant="contained">Cancel</ET.CancelButton>
					<ET.ConfirmButton data-testid={testIdPrefix ? `${testIdPrefix}-confirm` : undefined} variant="contained" onClick={handleSave}>{isEdit ? "Save" : "Add"}</ET.ConfirmButton>
				</ET.ButtonContainer>
			</ET.ActionContainer>

			<ET.DialogContent>
				<ET.InputContainer>
					<ET.LeftInputContainer>
						{leftFields.map(renderField)}
					</ET.LeftInputContainer>
					<ET.RightInputContainer>
						{rightFields.map(renderField)}
					</ET.RightInputContainer>
				</ET.InputContainer>
			</ET.DialogContent>
		</Dialog>
	);
};

AssetDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	closeHandler: PropTypes.func.isRequired,
	onSuccess: PropTypes.func.isRequired,
	getError: PropTypes.func.isRequired,
	isEdit: PropTypes.bool,
	showSAPColumns: PropTypes.bool,
	siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	data: PropTypes.object,
};

export default AssetDialog;
